import { useQuery } from '@apollo/client';
import React from 'react';
import Image from 'react-bootstrap/Image';
import { GetVanityPiecesQuery, VanityPieceType } from '../../../../../../__gqltypes__';
import { GET_VANITY_PIECES } from '../../../components/apolloQueries';
import SelectDropdown from '../../../components/SelectDropdown';
import { VANITY_PIECE_TYPE_LABELS } from '../utils';

type Props = {
  value: string | null;
  onSelect: (vanityPieceId: string | null) => void;
  type: VanityPieceType;
};

/**
 * Picks the vanity piece spent to unlock an item with the "PIECES" acquisition
 * (managed in Customization Items > Vanity Pieces). Only the pieces of the given vanity type are listed,
 * plus the pieces without type (created before types existed).
 */
export default function VanityPiecePicker({ value, onSelect, type }: Props) {
  const { data } = useQuery<GetVanityPiecesQuery>(GET_VANITY_PIECES);

  const options: Record<string, { value: React.ReactNode; searchValue: string }> = {};
  (data?.liveOps?.vanityPieces ?? [])
    // keep the selected piece visible even if its type does not match, the backend rejects it on save
    .filter((piece) => !piece.type || piece.type === type || piece.id === value)
    .forEach((piece) => {
      options[piece.id] = {
        value: (
          <>
            {piece.icon && (
              <Image roundedCircle className="img-profile mr-3" src={piece.icon} style={{ width: 20, height: 20 }} />
            )}
            {piece.name}
            {piece.type !== type && (
              <span className="text-muted ml-2">
                ({piece.type ? `${VANITY_PIECE_TYPE_LABELS[piece.type]} piece` : 'no type'})
              </span>
            )}
          </>
        ),
        searchValue: piece.name,
      };
    });

  return (
    <SelectDropdown
      title={`Select ${VANITY_PIECE_TYPE_LABELS[type].toLowerCase()} vanity piece`}
      options={options}
      value={value}
      onSelect={onSelect}
    />
  );
}
